/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  DropDown,
  Icons,
  PLACEMENT,
} from '@web-stories-wp/design-system';
import { __ } from '@web-stories-wp/i18n';

/**
 * Internal dependencies
 */
import StoryPropTypes from '../../types';
import {
  cancelDynamicCropRecording,
  completeDynamicCropRecording,
  useDynamicCropRecorderState,
} from './dynamicCropRecorder';
import {
  clamp,
  createDynamicCropAspectRatio,
  createDefaultDynamicCrop,
  getDynamicCropGeometry,
  normalizeDynamicCropPositions,
} from './dynamicCrop';

const CAPTURE_INTERVAL_MS = 100;
const MIN_CAPTURE_PLAYBACK_RATE = 0.1;
const RECORDER_CAPTURE_TIMESTAMP_TOLERANCE = 0.01;
const RECORDER_SEEK_PRUNE_TOLERANCE = 0.001;
const RECORDER_DEFAULT_CROP_HEIGHT = 1;
const RECORDER_MIN_CROP_HEIGHT = 0.2;
const RECORDER_KEYBOARD_STEP = 0.01;
const RECORDER_SCALE_KEYBOARD_STEP = 0.05;
const RECORDER_FRAME_RATE_FALLBACK = 30;
const RECORDER_CAPTURE_COMPLETION_TOLERANCE = 0.5;
const RECORDER_FRAME_BORDER_COLOR = '#84b3f9';
const RECORDER_FRAME_HANDLE_BACKGROUND = '#131516';
export const RECORDER_PLAYBACK_RATE_OPTIONS = [
  { label: '0.25x', value: 0.25 },
  { label: '0.5x', value: 0.5 },
  { label: '1x', value: 1 },
  { label: '1.5x', value: 1.5 },
  { label: '2x', value: 2 },
];

export const recordingSpeedMenuStyles = css`
  width: 88px;
  min-width: 88px;
  margin: 0;
  padding: 4px 0;

  li {
    grid-template-columns: 18px 1fr;
    min-height: 28px;
    margin: 2px 4px;
    padding: 5px 8px;
  }

  li svg {
    left: 2px;
    width: 18px;
    height: 18px;
  }

  li span {
    font-size: 13px;
    line-height: 1;
    white-space: nowrap;
  }
`;

const getFiniteMediaTime = (time) =>
  Number.isFinite(time) && time > 0 ? time : 0;

export const formatRecorderTime = (time) => {
  const safeTime = Math.max(Math.floor(getFiniteMediaTime(time)), 0);
  const hours = Math.floor(safeTime / 3600);
  const minutes = Math.floor((safeTime % 3600) / 60);
  const seconds = safeTime % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const getRecordedDuration = (positions) =>
  positions.reduce(
    (maxTimestamp, { timestamp }) =>
      Number.isFinite(timestamp)
        ? Math.max(maxTimestamp, timestamp)
        : maxTimestamp,
    0
  );

const isIncompleteRecording = (positions, expectedDuration) => {
  if (!expectedDuration) {
    return false;
  }

  if (!positions.length) {
    return true;
  }

  return (
    expectedDuration - getRecordedDuration(positions) >
    RECORDER_CAPTURE_COMPLETION_TOLERANCE
  );
};

const hasMatchingCropPosition = (positionA, positionB) =>
  positionA.xPositionPercent === positionB.xPositionPercent &&
  positionA.y === positionB.y;

export const RecordingShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 0;
  color: #fff;
  pointer-events: auto;
`;

export const RecordingVideoArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  overflow: hidden;
`;

export const RecordingStage = styled.div`
  position: relative;
  width: ${({ $fillWidth }) => ($fillWidth ? '100%' : 'auto')};
  height: ${({ $fillWidth }) => ($fillWidth ? 'auto' : '100%')};
  max-width: 100%;
  max-height: 100%;
  aspect-ratio: ${({ $sourceAspectRatio }) => $sourceAspectRatio};
  overflow: hidden;
  background: #000;
`;

export const RecordingVideo = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
`;

export const RecordingActionGroup = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RecordingActionButton = styled(Button).attrs({
  size: BUTTON_SIZES.SMALL,
  variant: BUTTON_VARIANTS.RECTANGLE,
})`
  justify-content: center;
  min-height: 32px;
  padding: 8px 20px;
  border-radius: 6px;
  font-family: ${({ theme }) => theme.typography.family.primary};
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.14px;
  white-space: nowrap;
`;

export const RecordingCancelButton = styled(RecordingActionButton).attrs({
  size: BUTTON_SIZES.SMALL,
  type: BUTTON_TYPES.SECONDARY,
  variant: BUTTON_VARIANTS.RECTANGLE,
})`
  color: #fff;
  background-color: rgba(0, 0, 0, 0.72);

  &:hover:not(:disabled),
  &:focus,
  &:active {
    color: #fff;
    background-color: rgba(0, 0, 0, 0.72);
  }
`;

export const RecordingDoneButton = styled(RecordingActionButton).attrs({
  type: BUTTON_TYPES.PRIMARY,
})``;

const RecordingCropFrame = styled.div`
  position: absolute;
  left: ${({ $xPosition }) => `${$xPosition * 100}%`};
  top: ${({ $yPosition }) => `${$yPosition * 100}%`};
  width: ${({ $cropWidthFraction }) => `${$cropWidthFraction * 100}%`};
  height: ${({ $cropHeight }) => `${$cropHeight * 100}%`};
  z-index: 2;
  border: 1px solid ${RECORDER_FRAME_BORDER_COLOR};
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.58);
  cursor: move;
  touch-action: none;
  pointer-events: auto;
  will-change: transform;
`;

const RecordingScaleHandle = styled.span`
  position: absolute;
  ${({ $position }) => ($position === 'top' ? 'top: 0;' : 'bottom: 0;')}
  left: 50%;
  width: 36px;
  height: 10px;
  z-index: 3;
  border: 1px solid ${RECORDER_FRAME_BORDER_COLOR};
  border-radius: 8px;
  background: ${RECORDER_FRAME_HANDLE_BACKGROUND};
  cursor: ns-resize;
  pointer-events: auto;
  touch-action: none;
  transform: translate(
    -50%,
    ${({ $position }) => ($position === 'top' ? '-50%' : '50%')}
  );
`;

const RecordingCountdownOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.18);
  pointer-events: none;
`;

const RecordingErrorMessage = styled.div`
  max-width: min(100%, 520px);
  padding: 6px 10px;
  border-radius: 4px;
  color: #fff;
  background: rgba(0, 0, 0, 0.62);
  font-size: 13px;
  line-height: 18px;
  text-align: center;
`;

const RecordingCountdownNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  box-sizing: border-box;
  font-size: 64px;
  font-weight: 700;
  line-height: 1;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  background: rgba(0, 0, 0, 0.58);
`;

export const RecordingControls = styled.div`
  width: 100%;
  flex: 0 0 auto;
  padding: 0;
  color: #fff;
  background: transparent;
  pointer-events: auto;
`;

export const RecordingControlsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(128px, 1fr) auto minmax(128px, 1fr);
  align-items: center;
  min-height: 42px;
`;

export const RecordingControlGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const RecordingControlButton = styled(Button).attrs({
  size: BUTTON_SIZES.SMALL,
  type: BUTTON_TYPES.TERTIARY,
  variant: BUTTON_VARIANTS.SQUARE,
})`
  color: ${({ theme }) => theme.colors.fg.primary};
  background: ${({ theme }) => theme.colors.interactiveBg.tertiaryNormal};

  &:hover:not(:disabled),
  &:focus {
    background: ${({ theme }) => theme.colors.interactiveBg.tertiaryHover};
  }

  &:disabled {
    opacity: 0.45;
  }

  svg {
    display: block;
  }
`;

export const RecordingPlayButton = styled(Button).attrs({
  size: BUTTON_SIZES.SMALL,
  type: BUTTON_TYPES.PLAIN,
  variant: BUTTON_VARIANTS.SQUARE,
})`
  color: ${({ theme }) => theme.colors.fg.primary};
  background: transparent;

  &:hover:not(:disabled),
  &:focus {
    background: ${({ theme }) => theme.colors.interactiveBg.tertiaryHover};
  }

  &:disabled {
    opacity: 0.45;
    background: transparent;
  }

  svg {
    display: block;
  }
`;

const RecordingFrameStartButton = styled(Button).attrs({
  size: BUTTON_SIZES.SMALL,
  type: BUTTON_TYPES.PRIMARY,
  variant: BUTTON_VARIANTS.RECTANGLE,
})`
  position: absolute;
  left: ${({ $xPosition, $cropWidthFraction }) =>
    `${(Number($xPosition) + Number($cropWidthFraction) / 2) * 100}%`};
  top: ${({ $yPosition, $cropHeight }) =>
    `${(Number($yPosition) + Number($cropHeight) / 2) * 100}%`};
  z-index: 3;
  min-height: 30px;
  padding: 6px 16px;
  border-radius: 4px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.32);
  transform: translate(-50%, -50%);
`;

export const RecordingFrameStepIcon = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  &::before {
    content: '';
    position: absolute;
    top: 9px;
    bottom: 9px;
    left: ${({ $direction }) => ($direction === 'previous' ? '7px' : 'auto')};
    right: ${({ $direction }) => ($direction === 'next' ? '7px' : 'auto')};
    width: 1px;
    border-radius: 1px;
    background: currentColor;
  }

  svg {
    transform: translateX(
      ${({ $direction }) => ($direction === 'previous' ? '2px' : '-2px')}
    );
  }
`;

export const RecordingTimeline = styled.input`
  width: 100%;
  height: 24px;
  margin: 0 0 8px;
  padding: 0;
  background: transparent;
  cursor: pointer;
  appearance: none;

  &::-webkit-slider-runnable-track {
    height: 4px;
    border: 0;
    border-radius: 8px;
    background: linear-gradient(
      to right,
      ${({ theme }) => theme.colors.primary} 0%,
      ${({ theme }) => theme.colors.primary} var(--recorder-progress, 0%),
      #4c5253 var(--recorder-progress, 0%),
      #4c5253 100%
    );
  }

  &::-webkit-slider-thumb {
    width: 24px;
    height: 24px;
    margin-top: -10px;
    border: 0;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.fg.primary};
    appearance: none;
  }

  &::-moz-range-track {
    height: 4px;
    border: 0;
    border-radius: 8px;
    background: #4c5253;
  }

  &::-moz-range-progress {
    height: 4px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.primary};
  }

  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border: 0;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.fg.primary};
  }

  &:disabled {
    cursor: default;
  }
`;

export const RecordingTime = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 128px;
  font-family: ${({ theme }) => theme.typography.family.primary};
  font-size: 13px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.82);
`;

export const RecordingTimeSeparator = styled.span`
  display: inline-block;
  width: 1px;
  height: 18px;
  background: rgba(114, 134, 188, 0.45);
`;

export const RecordingSpeedWrapper = styled.div`
  justify-self: end;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  width: auto;
`;

export const RecordingMuteButton = styled(RecordingControlButton)`
  svg {
    width: 20px;
    height: 20px;
  }
`;

export const RecordingSpeed = styled(DropDown)`
  width: 88px;

  > button {
    height: 32px;
    min-height: 32px;
    padding: 4px 0 4px 10px;
  }

  > button span {
    font-size: 13px;
    line-height: 1;
  }
`;

function DynamicCropRecorderDisplay({
  element,
  width,
  height,
  targetAspectRatio,
}) {
  const { resource } = element;
  const recorderState = useDynamicCropRecorderState();
  const recorderVideoRef = useRef();
  const recorderStageRef = useRef();
  const recorderFrameRef = useRef();
  const recorderTimelineRef = useRef();
  const recorderCurrentTimeRef = useRef();
  const recorderXPositionRef = useRef(0);
  const recorderCropHeightRef = useRef(1);
  const recorderCropWidthFractionRef = useRef(1);
  const recorderYPositionRef = useRef(0);
  const recordedPositionsRef = useRef([]);
  const isMountedRef = useRef(false);
  const [recorderXPosition, setRecorderXPosition] = useState(0);
  const [recorderCropHeight, setRecorderCropHeight] = useState(1);
  const [recorderYPosition, setRecorderYPosition] = useState(0);
  const [recorderCountdown, setRecorderCountdown] = useState(null);
  const [hasRecorderStarted, setHasRecorderStarted] = useState(false);
  const [isRecorderRecording, setIsRecorderRecording] = useState(false);
  const [isRecorderPlaying, setIsRecorderPlaying] = useState(false);
  const [isRecorderMuted, setIsRecorderMuted] = useState(
    Boolean(resource?.isMuted || resource?.noAudio)
  );
  const [recorderPlaybackRate, setRecorderPlaybackRate] = useState(1);
  const [recorderCurrentTime, setRecorderCurrentTime] = useState(0);
  const [recorderCaptureError, setRecorderCaptureError] = useState(null);
  const [recorderDuration, setRecorderDuration] = useState(
    getFiniteMediaTime(resource?.length)
  );
  const recorderAspectRatio = useMemo(
    () =>
      targetAspectRatio ||
      createDynamicCropAspectRatio(
        element.width ?? resource?.width,
        element.height ?? resource?.height
      ),
    [
      element.height,
      element.width,
      resource?.height,
      resource?.width,
      targetAspectRatio,
    ]
  );
  const recorderDynamicCrop = useMemo(
    () =>
      createDefaultDynamicCrop({
        aspectRatio: recorderAspectRatio,
        height: recorderCropHeight,
        sourceWidth: resource?.width,
        sourceHeight: resource?.height,
        xPositionPercent: recorderXPosition,
        y: recorderYPosition,
      }),
    [
      recorderCropHeight,
      recorderXPosition,
      recorderYPosition,
      recorderAspectRatio,
      resource?.height,
      resource?.width,
    ]
  );
  const recorderGeometry = useMemo(
    () =>
      getDynamicCropGeometry(
        recorderDynamicCrop,
        resource?.width,
        resource?.height
      ),
    [recorderDynamicCrop, resource?.height, resource?.width]
  );
  const recorderShouldFillWidth =
    !height || recorderGeometry.sourceAspectRatio >= width / height;
  const recorderVideoSourceKey = `${resource?.mimeType || ''}:${
    resource?.src || ''
  }`;

  const updateRecorderTimelineDisplay = useCallback(() => {
    const node = recorderVideoRef.current;
    if (!node) {
      return {
        currentTime: 0,
        duration: getFiniteMediaTime(resource?.length),
      };
    }

    const currentTime = getFiniteMediaTime(node.currentTime);
    const duration =
      getFiniteMediaTime(node.duration) || getFiniteMediaTime(resource?.length);
    const displayTime = duration
      ? Math.min(currentTime, duration)
      : currentTime;
    const progress = duration ? Math.min(displayTime / duration, 1) : 0;

    if (recorderTimelineRef.current) {
      recorderTimelineRef.current.value = String(displayTime);
      recorderTimelineRef.current.style.setProperty(
        '--recorder-progress',
        `${progress * 100}%`
      );
    }

    if (recorderCurrentTimeRef.current) {
      recorderCurrentTimeRef.current.textContent =
        formatRecorderTime(displayTime);
    }

    return {
      currentTime: displayTime,
      duration,
    };
  }, [resource?.length]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    recorderXPositionRef.current = recorderXPosition;
  }, [recorderXPosition]);

  useEffect(() => {
    recorderCropHeightRef.current = recorderCropHeight;
  }, [recorderCropHeight]);

  useEffect(() => {
    recorderCropWidthFractionRef.current = recorderGeometry.cropWidthFraction;
  }, [recorderGeometry.cropWidthFraction]);

  useEffect(() => {
    recorderYPositionRef.current = recorderYPosition;
  }, [recorderYPosition]);

  useEffect(() => {
    const fallbackCrop = createDefaultDynamicCrop({
      aspectRatio: recorderAspectRatio,
      height: RECORDER_DEFAULT_CROP_HEIGHT,
      sourceWidth: resource?.width,
      sourceHeight: resource?.height,
    });
    const initialCrop = element.dynamicCrop?.positions?.length
      ? {
          ...element.dynamicCrop,
          aspectRatio: recorderAspectRatio,
        }
      : fallbackCrop;
    const initialGeometry = getDynamicCropGeometry(
      initialCrop,
      resource?.width,
      resource?.height
    );
    const initialPosition =
      normalizeDynamicCropPositions(initialCrop)[0] ||
      fallbackCrop.positions[0];
    const initialXPosition = clamp(
      initialPosition.xPositionPercent,
      0,
      initialGeometry.maxXPosition
    );

    recorderXPositionRef.current = initialXPosition;
    recorderCropHeightRef.current = initialGeometry.cropHeight;
    recorderYPositionRef.current = initialGeometry.yPosition;
    recordedPositionsRef.current = [];
    setRecorderXPosition(initialXPosition);
    setRecorderCropHeight(initialGeometry.cropHeight);
    setRecorderYPosition(initialGeometry.yPosition);
    setRecorderCountdown(null);
    setHasRecorderStarted(false);
    setIsRecorderRecording(false);
    setIsRecorderPlaying(false);
    setIsRecorderMuted(Boolean(resource?.isMuted || resource?.noAudio));
    setRecorderPlaybackRate(1);
    setRecorderCurrentTime(0);
    setRecorderDuration(getFiniteMediaTime(resource?.length));
    setRecorderCaptureError(null);

    return () => {
      const node = recorderVideoRef.current;
      if (node) {
        try {
          node.pause();
        } catch {
          // jsdom does not implement media pause; browsers should not throw.
        }
      }
      recordedPositionsRef.current = [];
    };
  }, [
    element.id,
    element.dynamicCrop,
    recorderAspectRatio,
    recorderState.revision,
    resource?.height,
    resource?.isMuted,
    resource?.length,
    resource?.noAudio,
    resource?.src,
    resource?.width,
  ]);

  const updateRecorderTimelineState = useCallback(() => {
    const { currentTime, duration } = updateRecorderTimelineDisplay();

    setRecorderCurrentTime(currentTime);
    setRecorderDuration(duration);
  }, [updateRecorderTimelineDisplay]);

  const updateRecorderMediaState = useCallback(() => {
    const node = recorderVideoRef.current;
    if (!node) {
      return;
    }

    updateRecorderTimelineState();
    setIsRecorderPlaying(!node.paused && !node.ended);
  }, [updateRecorderTimelineState]);

  useEffect(() => {
    const node = recorderVideoRef.current;
    if (node) {
      node.playbackRate = recorderPlaybackRate;
    }
  }, [recorderPlaybackRate]);

  useEffect(() => {
    const node = recorderVideoRef.current;
    if (node) {
      node.muted = isRecorderMuted || Boolean(resource?.noAudio);
    }
  }, [isRecorderMuted, resource?.noAudio]);

  useEffect(() => {
    if (!isRecorderPlaying) {
      return undefined;
    }

    let animationFrameId;

    const updateFrame = () => {
      updateRecorderTimelineDisplay();
      animationFrameId = window.requestAnimationFrame(updateFrame);
    };

    animationFrameId = window.requestAnimationFrame(updateFrame);

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isRecorderPlaying, updateRecorderTimelineDisplay]);

  const getCurrentCapturePosition = useCallback((options = {}) => {
    const node = recorderVideoRef.current;
    if (!node || typeof node.currentTime !== 'number') {
      return null;
    }

    const timestamp = node.currentTime || 0;
    if (!Number.isFinite(timestamp)) {
      return null;
    }

    const xPositionPercent = recorderXPositionRef.current;
    if (!Number.isFinite(xPositionPercent)) {
      return null;
    }
    const y = recorderYPositionRef.current;
    if (!Number.isFinite(y)) {
      return null;
    }

    return {
      timestamp,
      xPositionPercent,
      y,
      isPausedMovement: Boolean(options.isPausedMovement),
      isUserEdited: Boolean(options.isUserEdited),
    };
  }, []);

  const pruneRecordedPositionsAfter = useCallback((timestamp) => {
    if (!Number.isFinite(timestamp)) {
      return;
    }

    recordedPositionsRef.current = recordedPositionsRef.current.filter(
      ({ timestamp: recordedTimestamp }) =>
        !Number.isFinite(recordedTimestamp) ||
        recordedTimestamp <= timestamp + RECORDER_SEEK_PRUNE_TOLERANCE
    );
  }, []);

  const sampleCurrentPosition = useCallback(
    (options = {}) => {
      const position = getCurrentCapturePosition(options);
      if (!position) {
        return false;
      }

      const recordedPositions = recordedPositionsRef.current;
      const hasMatchingNearbyPosition = recordedPositions.some(
        (recordedPosition) =>
          Math.abs(recordedPosition.timestamp - position.timestamp) <
            RECORDER_CAPTURE_TIMESTAMP_TOLERANCE &&
          hasMatchingCropPosition(recordedPosition, position)
      );

      if (hasMatchingNearbyPosition && !options.replaceNearby) {
        return false;
      }

      const nextRecordedPositions = recordedPositions.filter(
        ({ timestamp }) =>
          Math.abs(timestamp - position.timestamp) >=
          RECORDER_CAPTURE_TIMESTAMP_TOLERANCE
      );

      recordedPositionsRef.current = [...nextRecordedPositions, position].sort(
        (positionA, positionB) => positionA.timestamp - positionB.timestamp
      );
      return true;
    },
    [getCurrentCapturePosition]
  );

  const startRecorderPlayback = useCallback(() => {
    const node = recorderVideoRef.current;
    if (!node) {
      return;
    }

    recordedPositionsRef.current = [];
    setRecorderCaptureError(null);
    try {
      node.currentTime = 0;
    } catch {
      // Some media elements do not allow seeking before metadata is ready.
    }

    setRecorderCountdown(null);
    setIsRecorderRecording(true);
    setRecorderCurrentTime(0);
    updateRecorderTimelineDisplay();
    sampleCurrentPosition();

    node.play()?.catch(() => {
      if (!isMountedRef.current) {
        return;
      }

      setIsRecorderRecording(false);
      setIsRecorderPlaying(false);
    });
    setIsRecorderPlaying(true);
  }, [sampleCurrentPosition, updateRecorderTimelineDisplay]);

  const startRecorderCountdown = useCallback(() => {
    const node = recorderVideoRef.current;

    recordedPositionsRef.current = [];
    setRecorderCaptureError(null);
    setHasRecorderStarted(true);
    setRecorderCountdown(3);
    setIsRecorderRecording(false);
    setIsRecorderPlaying(false);
    setRecorderCurrentTime(0);

    if (node) {
      try {
        node.pause();
        node.currentTime = 0;
      } catch {
        // Some media elements do not allow seeking before metadata is ready.
      }
    }

    updateRecorderTimelineDisplay();
  }, [updateRecorderTimelineDisplay]);

  useEffect(() => {
    if (recorderCountdown === null) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      if (!isMountedRef.current) {
        return;
      }

      if (recorderCountdown > 1) {
        setRecorderCountdown(recorderCountdown - 1);
      } else {
        startRecorderPlayback();
      }
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [recorderCountdown, startRecorderPlayback]);

  useEffect(() => {
    if (!isRecorderRecording) {
      return undefined;
    }

    const playbackRate = Math.max(
      Number.isFinite(recorderPlaybackRate) ? recorderPlaybackRate : 1,
      MIN_CAPTURE_PLAYBACK_RATE
    );
    const intervalMs =
      playbackRate >= 1
        ? CAPTURE_INTERVAL_MS
        : CAPTURE_INTERVAL_MS / playbackRate;
    const intervalId = window.setInterval(sampleCurrentPosition, intervalMs);

    return () => window.clearInterval(intervalId);
  }, [isRecorderRecording, recorderPlaybackRate, sampleCurrentPosition]);

  const completeRecorderCapture = useCallback(() => {
    const node = recorderVideoRef.current;
    sampleCurrentPosition();
    const positions = [...recordedPositionsRef.current].sort(
      (positionA, positionB) => positionA.timestamp - positionB.timestamp
    );
    const expectedDuration =
      getFiniteMediaTime(node?.duration) ||
      getFiniteMediaTime(resource?.length);

    if (isIncompleteRecording(positions, expectedDuration)) {
      recordedPositionsRef.current = [];
      setIsRecorderRecording(false);
      setIsRecorderPlaying(false);
      setRecorderCountdown(null);
      setHasRecorderStarted(false);
      setRecorderCurrentTime(0);
      setRecorderCaptureError(
        __(
          'Recording did not cover the full video. Please try again.',
          'web-stories'
        )
      );

      if (node) {
        try {
          node.currentTime = 0;
        } catch {
          // Some media elements do not allow seeking after an incomplete capture.
        }
      }
      updateRecorderTimelineDisplay();
      return;
    }

    const initialYPosition =
      positions.find(({ y }) => Number.isFinite(y))?.y ??
      recorderYPositionRef.current;
    recordedPositionsRef.current = [];
    setRecorderCaptureError(null);
    const dynamicCrop = {
      aspectRatio: recorderAspectRatio,
      height: recorderCropHeightRef.current,
      y: initialYPosition,
      positions,
    };
    setIsRecorderRecording(false);
    setIsRecorderPlaying(false);
    setRecorderCountdown(null);
    setHasRecorderStarted(false);
    completeDynamicCropRecording(dynamicCrop);
  }, [
    recorderAspectRatio,
    resource?.length,
    sampleCurrentPosition,
    updateRecorderTimelineDisplay,
  ]);

  const seekRecorderTo = useCallback(
    (time) => {
      const node = recorderVideoRef.current;
      if (!node) {
        return;
      }

      const duration =
        getFiniteMediaTime(node.duration) ||
        getFiniteMediaTime(resource?.length);
      const nextTime = duration
        ? clamp(getFiniteMediaTime(time), 0, duration)
        : getFiniteMediaTime(time);

      try {
        node.currentTime = nextTime;
      } catch {
        return;
      }

      pruneRecordedPositionsAfter(nextTime);
      updateRecorderTimelineDisplay();
      setRecorderCurrentTime(nextTime);
    },
    [
      pruneRecordedPositionsAfter,
      resource?.length,
      updateRecorderTimelineDisplay,
    ]
  );

  const toggleRecorderPlayback = useCallback(() => {
    if (recorderCountdown !== null) {
      return;
    }

    if (!hasRecorderStarted) {
      startRecorderCountdown();
      return;
    }

    const node = recorderVideoRef.current;
    if (!node) {
      return;
    }

    if (isRecorderPlaying) {
      node.pause();
      setIsRecorderPlaying(false);
      setIsRecorderRecording(false);
      sampleCurrentPosition();
      return;
    }

    const duration =
      getFiniteMediaTime(node.duration) || getFiniteMediaTime(resource?.length);
    if (duration && node.currentTime >= duration) {
      seekRecorderTo(0);
    }

    setIsRecorderRecording(true);
    sampleCurrentPosition();
    node.play()?.catch(() => {
      if (!isMountedRef.current) {
        return;
      }

      setIsRecorderRecording(false);
      setIsRecorderPlaying(false);
    });
    setIsRecorderPlaying(true);
  }, [
    hasRecorderStarted,
    isRecorderPlaying,
    recorderCountdown,
    resource?.length,
    sampleCurrentPosition,
    seekRecorderTo,
    startRecorderCountdown,
  ]);

  const handleRecorderStageClick = useCallback(
    (event) => {
      if (!hasRecorderStarted) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (
        recorderFrameRef.current?.contains(target) ||
        target.closest('button')
      ) {
        return;
      }

      toggleRecorderPlayback();
    },
    [hasRecorderStarted, toggleRecorderPlayback]
  );

  const handleTimelineChange = useCallback(
    (event) => {
      seekRecorderTo(Number(event.target.value));
    },
    [seekRecorderTo]
  );

  const handleFrameStep = useCallback(
    (direction) => {
      const frameDuration = 1 / RECORDER_FRAME_RATE_FALLBACK;
      const node = recorderVideoRef.current;
      const currentTime = getFiniteMediaTime(node?.currentTime);

      seekRecorderTo(currentTime + frameDuration * direction);
    },
    [seekRecorderTo]
  );

  const handlePlaybackRateChange = useCallback((_event, value) => {
    setRecorderPlaybackRate(Number(value));
  }, []);

  const toggleRecorderMuted = useCallback(() => {
    if (resource?.noAudio) {
      return;
    }

    setIsRecorderMuted((muted) => !muted);
  }, [resource?.noAudio]);

  const updateRecorderXPosition = useCallback(
    (nextXPosition) => {
      if (!Number.isFinite(nextXPosition)) {
        return;
      }

      const xPosition = clamp(nextXPosition, 0, recorderGeometry.maxXPosition);
      recorderXPositionRef.current = xPosition;
      setRecorderXPosition(xPosition);
    },
    [recorderGeometry.maxXPosition]
  );

  const updateRecorderYPosition = useCallback(
    (nextYPosition) => {
      if (!Number.isFinite(nextYPosition)) {
        return;
      }

      const yPosition = clamp(nextYPosition, 0, recorderGeometry.maxYPosition);
      recorderYPositionRef.current = yPosition;
      setRecorderYPosition(yPosition);
    },
    [recorderGeometry.maxYPosition]
  );

  const updateRecorderCropHeight = useCallback(
    (nextCropHeight) => {
      if (!Number.isFinite(nextCropHeight) || hasRecorderStarted) {
        return;
      }

      const cropHeight = clamp(nextCropHeight, RECORDER_MIN_CROP_HEIGHT, 1);
      const currentCropCenterX =
        recorderXPositionRef.current + recorderCropWidthFractionRef.current / 2;
      const currentCropCenterY =
        recorderYPositionRef.current + recorderCropHeightRef.current / 2;
      const yPosition = currentCropCenterY - cropHeight / 2;
      const nextGeometry = getDynamicCropGeometry(
        createDefaultDynamicCrop({
          aspectRatio: recorderAspectRatio,
          height: cropHeight,
          sourceWidth: resource?.width,
          sourceHeight: resource?.height,
          y: yPosition,
        }),
        resource?.width,
        resource?.height
      );
      const xPosition = clamp(
        currentCropCenterX - nextGeometry.cropWidthFraction / 2,
        0,
        nextGeometry.maxXPosition
      );

      recorderCropHeightRef.current = nextGeometry.cropHeight;
      recorderCropWidthFractionRef.current = nextGeometry.cropWidthFraction;
      recorderYPositionRef.current = nextGeometry.yPosition;
      recorderXPositionRef.current = xPosition;
      setRecorderCropHeight(nextGeometry.cropHeight);
      setRecorderYPosition(nextGeometry.yPosition);
      setRecorderXPosition(xPosition);
    },
    [hasRecorderStarted, recorderAspectRatio, resource?.height, resource?.width]
  );

  const samplePausedUserEditPosition = useCallback(() => {
    if (!hasRecorderStarted || isRecorderPlaying) {
      return;
    }

    sampleCurrentPosition({
      isPausedMovement: true,
      isUserEdited: true,
      replaceNearby: true,
    });
  }, [hasRecorderStarted, isRecorderPlaying, sampleCurrentPosition]);

  const handleRecorderScalePointerDown = useCallback(
    (downEvent, position) => {
      if (hasRecorderStarted) {
        return;
      }

      const stage = recorderStageRef.current;
      if (!stage) {
        return;
      }

      const { height: sourceDisplayHeight } = stage.getBoundingClientRect();
      if (!sourceDisplayHeight) {
        return;
      }

      downEvent.preventDefault();
      downEvent.stopPropagation();

      const ownerDocument = stage.ownerDocument || document;
      const startClientY = downEvent.clientY;
      const startCropHeight = recorderCropHeightRef.current;

      const handlePointerMove = (event) => {
        const deltaYFraction =
          (event.clientY - startClientY) / sourceDisplayHeight;
        const heightDirection = position === 'top' ? -1 : 1;
        const cropHeight =
          startCropHeight + deltaYFraction * heightDirection * 2;

        updateRecorderCropHeight(cropHeight);
        event.preventDefault();
        event.stopPropagation();
      };

      const handlePointerUp = (event) => {
        ownerDocument.removeEventListener('pointermove', handlePointerMove);
        ownerDocument.removeEventListener('pointerup', handlePointerUp);
        ownerDocument.removeEventListener('pointercancel', handlePointerUp);
        event.preventDefault();
        event.stopPropagation();
      };

      ownerDocument.addEventListener('pointermove', handlePointerMove);
      ownerDocument.addEventListener('pointerup', handlePointerUp);
      ownerDocument.addEventListener('pointercancel', handlePointerUp);
    },
    [hasRecorderStarted, updateRecorderCropHeight]
  );

  const handleRecorderPointerDown = useCallback(
    (downEvent) => {
      if (
        recorderGeometry.maxXPosition <= 0 &&
        recorderGeometry.maxYPosition <= 0
      ) {
        return;
      }

      const frame = recorderFrameRef.current;
      const stage = recorderStageRef.current;
      if (!frame || !stage) {
        return;
      }

      const { width: sourceDisplayWidth, height: sourceDisplayHeight } =
        stage.getBoundingClientRect();
      if (!sourceDisplayWidth || !sourceDisplayHeight) {
        return;
      }

      downEvent.preventDefault();
      downEvent.stopPropagation();

      const ownerDocument = frame.ownerDocument || document;
      const startClientX = downEvent.clientX;
      const startClientY = downEvent.clientY;
      const startXPosition = recorderXPositionRef.current;
      const startYPosition = recorderYPositionRef.current;
      let dragAnimationFrameId = null;
      let nextXPosition = startXPosition;
      let nextYPosition = startYPosition;
      let nextDeltaX = 0;
      let nextDeltaY = 0;

      const applyDragPosition = () => {
        recorderXPositionRef.current = nextXPosition;
        recorderYPositionRef.current = nextYPosition;
        frame.style.transform = `translate3d(${nextDeltaX}px, ${nextDeltaY}px, 0)`;
        dragAnimationFrameId = null;
      };

      const handlePointerMove = (event) => {
        const deltaXPosition =
          (event.clientX - startClientX) / sourceDisplayWidth;
        const xPosition = clamp(
          startXPosition + deltaXPosition,
          0,
          recorderGeometry.maxXPosition
        );
        const deltaX = Number(
          ((xPosition - startXPosition) * sourceDisplayWidth).toFixed(3)
        );

        nextXPosition = xPosition;
        nextDeltaX = deltaX;
        const deltaYPosition =
          (event.clientY - startClientY) / sourceDisplayHeight;
        const yPosition = clamp(
          startYPosition + deltaYPosition,
          0,
          recorderGeometry.maxYPosition
        );
        const deltaY = Number(
          ((yPosition - startYPosition) * sourceDisplayHeight).toFixed(3)
        );

        nextYPosition = yPosition;
        nextDeltaY = deltaY;

        if (!hasRecorderStarted) {
          recorderXPositionRef.current = xPosition;
          recorderYPositionRef.current = yPosition;
          setRecorderXPosition(xPosition);
          setRecorderYPosition(yPosition);
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        recorderXPositionRef.current = xPosition;
        recorderYPositionRef.current = yPosition;
        if (dragAnimationFrameId === null) {
          dragAnimationFrameId =
            window.requestAnimationFrame(applyDragPosition);
        }
        event.preventDefault();
        event.stopPropagation();
      };

      const handlePointerUp = (event) => {
        ownerDocument.removeEventListener('pointermove', handlePointerMove);
        ownerDocument.removeEventListener('pointerup', handlePointerUp);
        ownerDocument.removeEventListener('pointercancel', handlePointerUp);
        if (dragAnimationFrameId !== null) {
          window.cancelAnimationFrame(dragAnimationFrameId);
          applyDragPosition();
        }
        frame.style.transform = '';
        setRecorderXPosition(recorderXPositionRef.current);
        setRecorderYPosition(recorderYPositionRef.current);
        samplePausedUserEditPosition();
        event.preventDefault();
        event.stopPropagation();
      };

      ownerDocument.addEventListener('pointermove', handlePointerMove);
      ownerDocument.addEventListener('pointerup', handlePointerUp);
      ownerDocument.addEventListener('pointercancel', handlePointerUp);
    },
    [
      hasRecorderStarted,
      recorderGeometry.maxXPosition,
      recorderGeometry.maxYPosition,
      samplePausedUserEditPosition,
    ]
  );

  const handleRecorderKeyDown = useCallback(
    (event) => {
      if (event.key === 'ArrowLeft') {
        updateRecorderXPosition(
          recorderXPositionRef.current - RECORDER_KEYBOARD_STEP
        );
        samplePausedUserEditPosition();
        event.preventDefault();
      } else if (event.key === 'ArrowRight') {
        updateRecorderXPosition(
          recorderXPositionRef.current + RECORDER_KEYBOARD_STEP
        );
        samplePausedUserEditPosition();
        event.preventDefault();
      } else if (
        !hasRecorderStarted &&
        event.shiftKey &&
        event.key === 'ArrowUp'
      ) {
        updateRecorderCropHeight(
          recorderCropHeightRef.current - RECORDER_SCALE_KEYBOARD_STEP
        );
        event.preventDefault();
      } else if (
        !hasRecorderStarted &&
        event.shiftKey &&
        event.key === 'ArrowDown'
      ) {
        updateRecorderCropHeight(
          recorderCropHeightRef.current + RECORDER_SCALE_KEYBOARD_STEP
        );
        event.preventDefault();
      } else if (event.key === 'ArrowUp') {
        updateRecorderYPosition(
          recorderYPositionRef.current - RECORDER_KEYBOARD_STEP
        );
        samplePausedUserEditPosition();
        event.preventDefault();
      } else if (event.key === 'ArrowDown') {
        updateRecorderYPosition(
          recorderYPositionRef.current + RECORDER_KEYBOARD_STEP
        );
        samplePausedUserEditPosition();
        event.preventDefault();
      }
    },
    [
      hasRecorderStarted,
      samplePausedUserEditPosition,
      updateRecorderCropHeight,
      updateRecorderXPosition,
      updateRecorderYPosition,
    ]
  );

  const recorderTimelineProgress = recorderDuration
    ? Math.min(recorderCurrentTime / recorderDuration, 1)
    : 0;

  return (
    <RecordingShell data-testid="dynamicCropRecorderShell">
      <RecordingVideoArea>
        <RecordingStage
          ref={recorderStageRef}
          $fillWidth={recorderShouldFillWidth}
          $sourceAspectRatio={recorderGeometry.sourceAspectRatio}
          data-testid="dynamicCropRecorderStage"
          onClick={handleRecorderStageClick}
        >
          <RecordingVideo
            key={recorderVideoSourceKey}
            ref={recorderVideoRef}
            playsInline
            preload="auto"
            muted={isRecorderMuted || Boolean(resource?.noAudio)}
            data-testid="dynamicCropRecorderVideo"
            onEnded={completeRecorderCapture}
            onLoadedData={updateRecorderMediaState}
            onLoadedMetadata={updateRecorderMediaState}
            onDurationChange={updateRecorderMediaState}
            onTimeUpdate={updateRecorderTimelineDisplay}
            onPlay={updateRecorderMediaState}
            onPause={updateRecorderMediaState}
          >
            {resource.src && (
              <>
                {/* Trick to get .mov files to play in Chrome */}
                <source src={resource.src} type={resource.mimeType} />
                <source src={resource.src} />
              </>
            )}
          </RecordingVideo>
          <RecordingActionGroup>
            <RecordingCancelButton onClick={cancelDynamicCropRecording}>
              {__('Cancel', 'web-stories')}
            </RecordingCancelButton>
          </RecordingActionGroup>
          <RecordingCropFrame
            ref={recorderFrameRef}
            role="slider"
            tabIndex={0}
            aria-label={__('Crop frame', 'web-stories')}
            aria-valuemin={0}
            aria-valuemax={recorderGeometry.maxXPosition}
            aria-valuenow={recorderXPosition}
            $xPosition={recorderXPosition}
            $yPosition={recorderGeometry.yPosition}
            $cropWidthFraction={recorderGeometry.cropWidthFraction}
            $cropHeight={recorderGeometry.cropHeight}
            onPointerDown={handleRecorderPointerDown}
            onKeyDown={handleRecorderKeyDown}
          >
            {!hasRecorderStarted && (
              <>
                <RecordingScaleHandle
                  aria-hidden="true"
                  data-testid="dynamicCropScaleTopHandle"
                  $position="top"
                  onPointerDown={(event) =>
                    handleRecorderScalePointerDown(event, 'top')
                  }
                />
                <RecordingScaleHandle
                  aria-hidden="true"
                  data-testid="dynamicCropScaleBottomHandle"
                  $position="bottom"
                  onPointerDown={(event) =>
                    handleRecorderScalePointerDown(event, 'bottom')
                  }
                />
              </>
            )}
          </RecordingCropFrame>
          {!hasRecorderStarted && (
            <RecordingFrameStartButton
              $xPosition={recorderXPosition}
              $yPosition={recorderGeometry.yPosition}
              $cropWidthFraction={recorderGeometry.cropWidthFraction}
              $cropHeight={recorderGeometry.cropHeight}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                startRecorderCountdown();
              }}
              disabled={recorderCountdown !== null}
            >
              {__('Start', 'web-stories')}
            </RecordingFrameStartButton>
          )}
          {recorderCountdown !== null && (
            <RecordingCountdownOverlay aria-live="polite">
              <RecordingCountdownNumber>
                {recorderCountdown}
              </RecordingCountdownNumber>
            </RecordingCountdownOverlay>
          )}
        </RecordingStage>
      </RecordingVideoArea>
      {recorderCaptureError && (
        <RecordingErrorMessage role="alert">
          {recorderCaptureError}
        </RecordingErrorMessage>
      )}
      <RecordingControls
        aria-label={__('Dynamic crop video controls', 'web-stories')}
      >
        <RecordingTimeline
          ref={recorderTimelineRef}
          type="range"
          aria-label={__('Playback timeline', 'web-stories')}
          min={0}
          max={recorderDuration || 0}
          step={0.01}
          value={Math.min(recorderCurrentTime, recorderDuration || 0)}
          onChange={handleTimelineChange}
          disabled={
            !hasRecorderStarted ||
            recorderCountdown !== null ||
            recorderDuration <= 0
          }
          style={{
            '--recorder-progress': `${recorderTimelineProgress * 100}%`,
          }}
        />
        <RecordingControlsRow>
          <RecordingTime>
            <span ref={recorderCurrentTimeRef}>
              {formatRecorderTime(recorderCurrentTime)}
            </span>
            <RecordingTimeSeparator aria-hidden="true" />
            <span>{formatRecorderTime(recorderDuration)}</span>
          </RecordingTime>
          <RecordingControlGroup>
            <RecordingControlButton
              aria-label={__('Previous frame', 'web-stories')}
              onClick={() => handleFrameStep(-1)}
              disabled={recorderCountdown !== null || recorderDuration <= 0}
            >
              <RecordingFrameStepIcon $direction="previous" aria-hidden="true">
                <Icons.ArrowLeft />
              </RecordingFrameStepIcon>
            </RecordingControlButton>
            <RecordingPlayButton
              aria-label={
                !hasRecorderStarted
                  ? __('Start recording playback', 'web-stories')
                  : isRecorderPlaying
                  ? __('Pause recording playback', 'web-stories')
                  : __('Play recording playback', 'web-stories')
              }
              onClick={toggleRecorderPlayback}
              disabled={recorderCountdown !== null}
            >
              {isRecorderPlaying ? (
                <Icons.StopOutline />
              ) : (
                <Icons.PlayOutline />
              )}
            </RecordingPlayButton>
            <RecordingControlButton
              aria-label={__('Next frame', 'web-stories')}
              onClick={() => handleFrameStep(1)}
              disabled={recorderCountdown !== null || recorderDuration <= 0}
            >
              <RecordingFrameStepIcon $direction="next" aria-hidden="true">
                <Icons.ArrowRight />
              </RecordingFrameStepIcon>
            </RecordingControlButton>
          </RecordingControlGroup>
          <RecordingSpeedWrapper>
            <RecordingMuteButton
              aria-label={
                isRecorderMuted
                  ? __('Unmute playback', 'web-stories')
                  : __('Mute playback', 'web-stories')
              }
              aria-pressed={isRecorderMuted}
              onClick={toggleRecorderMuted}
              disabled={Boolean(resource?.noAudio)}
            >
              {isRecorderMuted ? <Icons.Muted /> : <Icons.Unmuted />}
            </RecordingMuteButton>
            <RecordingSpeed
              ariaLabel={__('Playback speed', 'web-stories')}
              dropDownLabel=""
              placement={PLACEMENT.TOP_END}
              popupFillWidth={false}
              popupZIndex={30}
              menuStylesOverride={recordingSpeedMenuStyles}
              options={RECORDER_PLAYBACK_RATE_OPTIONS}
              selectedValue={recorderPlaybackRate}
              onMenuItemClick={handlePlaybackRateChange}
              disabled={recorderCountdown !== null}
            />
          </RecordingSpeedWrapper>
        </RecordingControlsRow>
      </RecordingControls>
    </RecordingShell>
  );
}

DynamicCropRecorderDisplay.propTypes = {
  element: StoryPropTypes.elements.video.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  targetAspectRatio: PropTypes.string,
};

export default DynamicCropRecorderDisplay;
